/**
 * ============================================================================
 * Iron Meridian - Geographic Data Loader
 * ============================================================================
 * 
 * Loads and processes TopoJSON world data from Natural Earth.
 * Converts to GeoJSON for D3 rendering with ISO Alpha-3 country codes.
 */

import * as topojson from 'topojson-client'

// Natural Earth 110m world data URL (from unpkg CDN)
const WORLD_TOPO_URL = 'https://unpkg.com/world-atlas@2/countries-110m.json'

// Country ID mapping from numeric codes to ISO Alpha-3
// Natural Earth uses numeric UN M.49 codes, we need ISO Alpha-3
const NUMERIC_TO_ISO = {
  4: 'AFG', 8: 'ALB', 12: 'DZA', 24: 'AGO', 32: 'ARG', 36: 'AUS', 40: 'AUT',
  50: 'BGD', 56: 'BEL', 68: 'BOL', 76: 'BRA', 100: 'BGR', 104: 'MMR',
  116: 'KHM', 120: 'CMR', 124: 'CAN', 144: 'LKA', 152: 'CHL', 156: 'CHN',
  170: 'COL', 178: 'COG', 180: 'COD', 188: 'CRI', 191: 'HRV', 192: 'CUB',
  196: 'CYP', 203: 'CZE', 208: 'DNK', 214: 'DOM', 218: 'ECU', 818: 'EGY',
  222: 'SLV', 226: 'GNQ', 231: 'ETH', 232: 'ERI', 233: 'EST', 246: 'FIN',
  250: 'FRA', 262: 'DJI', 266: 'GAB', 268: 'GEO', 276: 'DEU', 288: 'GHA',
  300: 'GRC', 320: 'GTM', 324: 'GIN', 328: 'GUY', 332: 'HTI', 340: 'HND',
  348: 'HUN', 352: 'ISL', 356: 'IND', 360: 'IDN', 364: 'IRN', 368: 'IRQ',
  372: 'IRL', 376: 'ISR', 380: 'ITA', 384: 'CIV', 388: 'JAM', 392: 'JPN',
  398: 'KAZ', 400: 'JOR', 404: 'KEN', 408: 'PRK', 410: 'KOR', 414: 'KWT',
  417: 'KGZ', 418: 'LAO', 422: 'LBN', 426: 'LSO', 428: 'LVA', 430: 'LBR',
  434: 'LBY', 440: 'LTU', 442: 'LUX', 450: 'MDG', 454: 'MWI', 458: 'MYS',
  466: 'MLI', 478: 'MRT', 484: 'MEX', 496: 'MNG', 498: 'MDA', 504: 'MAR',
  508: 'MOZ', 512: 'OMN', 516: 'NAM', 524: 'NPL', 528: 'NLD', 540: 'NCL',
  554: 'NZL', 558: 'NIC', 562: 'NER', 566: 'NGA', 578: 'NOR', 586: 'PAK',
  591: 'PAN', 598: 'PNG', 600: 'PRY', 604: 'PER', 608: 'PHL', 616: 'POL',
  620: 'PRT', 630: 'PRI', 634: 'QAT', 642: 'ROU', 643: 'RUS', 646: 'RWA',
  682: 'SAU', 686: 'SEN', 688: 'SRB', 694: 'SLE', 702: 'SGP', 703: 'SVK',
  704: 'VNM', 705: 'SVN', 706: 'SOM', 710: 'ZAF', 716: 'ZWE', 724: 'ESP',
  728: 'SSD', 729: 'SDN', 732: 'ESH', 740: 'SUR', 748: 'SWZ', 752: 'SWE',
  756: 'CHE', 760: 'SYR', 762: 'TJK', 764: 'THA', 768: 'TGO', 780: 'TTO',
  784: 'ARE', 788: 'TUN', 792: 'TUR', 795: 'TKM', 800: 'UGA', 804: 'UKR',
  807: 'MKD', 818: 'EGY', 826: 'GBR', 834: 'TZA', 840: 'USA', 854: 'BFA',
  858: 'URY', 860: 'UZB', 862: 'VEN', 887: 'YEM', 894: 'ZMB',
  // Additional mappings for territories
  10: 'ATA', 74: 'BVT', 162: 'CXI', 166: 'CCK', 184: 'COK', 238: 'FLK',
  239: 'SGS', 242: 'FJI', 254: 'GUF', 258: 'PYF', 260: 'ATF', 304: 'GRL',
  312: 'GLP', 316: 'GUM', 334: 'HMD', 344: 'HKG', 474: 'MTQ', 480: 'MUS',
  175: 'MYT', 583: 'FSM', 584: 'MHL', 585: 'PLW', 612: 'PCN', 638: 'REU',
  652: 'BLM', 654: 'SHN', 659: 'KNA', 660: 'AIA', 662: 'LCA', 663: 'MAF',
  666: 'SPM', 670: 'VCT', 882: 'WSM', 876: 'WLF', 90: 'SLB', 548: 'VUT',
  // Kosovo special case
  '-99': 'XKX',
}

// Mapping for territories with null IDs (use name property)
const NAME_TO_ISO = {
  'Somaliland': 'SOL',      // Unrecognized territory near Somalia
  'N. Cyprus': 'CYN',       // Northern Cyprus
  'Kosovo': 'XKX',          // Kosovo (also mapped from -99)
}

// Cache for loaded data
let geoDataCache = null

// Export a sorted list of the ISO Alpha-3 codes represented by the mapping.
// Useful for debugging which countries can be selected on the globe.
export const AVAILABLE_ISO_CODES = (() => {
  const codes = Object.values(NUMERIC_TO_ISO).filter(Boolean)
  return Array.from(new Set(codes)).sort()
})()

export function getAvailableIsoCodes() {
  return AVAILABLE_ISO_CODES
}

/**
 * Load world geography data
 * @returns {Promise<{countries: GeoJSON.FeatureCollection, land: GeoJSON.FeatureCollection}>}
 */
export async function loadWorldGeo() {
  if (geoDataCache) {
    return geoDataCache
  }

  try {
    const response = await fetch(WORLD_TOPO_URL)
    if (!response.ok) {
      throw new Error(`Failed to load world data: ${response.status}`)
    }
    
    const world = await response.json()
    
    // Convert TopoJSON to GeoJSON
    const countries = topojson.feature(world, world.objects.countries)
    const land = topojson.feature(world, world.objects.land)
    
    // Add ISO Alpha-3 codes to each country feature
    countries.features = countries.features.map(feature => {
      const rawId = feature.id ?? feature.properties?.id
      const name = feature.properties?.name
      
      let isoCode
      
      // Handle null/undefined IDs by looking up by name
      if (rawId == null || rawId === '') {
        isoCode = NAME_TO_ISO[name] || null
        // Skip territories we don't have a mapping for
        if (!isoCode) {
          console.warn(`[geoLoader] Skipping territory with no ID: ${name}`)
          return null
        }
      } else {
        // TopoJSON IDs are strings (e.g., "076"), convert to integer for lookup
        const numericId = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId
        isoCode = NUMERIC_TO_ISO[numericId] || NUMERIC_TO_ISO[rawId] || `UNK${rawId}`
      }
      
      return {
        ...feature,
        id: isoCode,
        properties: {
          ...feature.properties,
          id: isoCode,
          name: name || isoCode
        }
      }
    }).filter(Boolean) // Remove null entries

    geoDataCache = { countries, land }
    return geoDataCache
  } catch (error) {
    console.error('Failed to load world geography:', error)
    throw error
  }
}

/**
 * Get country name from ISO code
 * @param {string} isoCode 
 * @returns {string}
 */
export function getCountryName(isoCode) {
  const NAMES = {
    // Major powers
    'USA': 'United States', 'CAN': 'Canada', 'MEX': 'Mexico', 'BRA': 'Brazil',
    'ARG': 'Argentina', 'GBR': 'United Kingdom', 'FRA': 'France', 'DEU': 'Germany',
    'RUS': 'Russia', 'CHN': 'China', 'JPN': 'Japan', 'IND': 'India',
    'AUS': 'Australia', 'ZAF': 'South Africa', 'EGY': 'Egypt', 'SAU': 'Saudi Arabia',
    'TUR': 'Turkey', 'IRN': 'Iran', 'ESP': 'Spain', 'ITA': 'Italy',
    'POL': 'Poland', 'KOR': 'South Korea',
    
    // Africa
    'DZA': 'Algeria', 'AGO': 'Angola', 'BEN': 'Benin', 'BWA': 'Botswana',
    'BFA': 'Burkina Faso', 'BDI': 'Burundi', 'CMR': 'Cameroon', 'COG': 'Congo',
    'COD': 'DR Congo', 'DJI': 'Djibouti', 'ERI': 'Eritrea', 'SWZ': 'Eswatini',
    'ETH': 'Ethiopia', 'GAB': 'Gabon', 'GHA': 'Ghana', 'GIN': 'Guinea',
    'CIV': 'Ivory Coast', 'KEN': 'Kenya', 'LSO': 'Lesotho', 'LBR': 'Liberia',
    'LBY': 'Libya', 'MDG': 'Madagascar', 'MWI': 'Malawi', 'MLI': 'Mali',
    'MRT': 'Mauritania', 'MUS': 'Mauritius', 'MAR': 'Morocco', 'MOZ': 'Mozambique',
    'NAM': 'Namibia', 'NER': 'Niger', 'NGA': 'Nigeria', 'RWA': 'Rwanda',
    'SEN': 'Senegal', 'SLE': 'Sierra Leone', 'SOM': 'Somalia', 'SSD': 'South Sudan',
    'SDN': 'Sudan', 'TZA': 'Tanzania', 'TGO': 'Togo', 'TUN': 'Tunisia',
    'UGA': 'Uganda', 'ZMB': 'Zambia', 'ZWE': 'Zimbabwe', 'GNQ': 'Equatorial Guinea',
    'ESH': 'Western Sahara',
    
    // Americas
    'BOL': 'Bolivia', 'CHL': 'Chile', 'COL': 'Colombia', 'CRI': 'Costa Rica',
    'CUB': 'Cuba', 'DOM': 'Dominican Republic', 'ECU': 'Ecuador', 'SLV': 'El Salvador',
    'GTM': 'Guatemala', 'GUY': 'Guyana', 'HTI': 'Haiti', 'HND': 'Honduras',
    'JAM': 'Jamaica', 'NIC': 'Nicaragua', 'PAN': 'Panama', 'PRY': 'Paraguay',
    'PER': 'Peru', 'SUR': 'Suriname', 'TTO': 'Trinidad and Tobago', 'URY': 'Uruguay',
    'VEN': 'Venezuela', 'PRI': 'Puerto Rico', 'GRL': 'Greenland',
    
    // Asia
    'AFG': 'Afghanistan', 'ARM': 'Armenia', 'AZE': 'Azerbaijan', 'BGD': 'Bangladesh',
    'KHM': 'Cambodia', 'CYP': 'Cyprus', 'GEO': 'Georgia', 'IRQ': 'Iraq',
    'ISR': 'Israel', 'JOR': 'Jordan', 'KWT': 'Kuwait', 'KGZ': 'Kyrgyzstan',
    'LAO': 'Laos', 'LBN': 'Lebanon', 'MYS': 'Malaysia', 'MNG': 'Mongolia',
    'MMR': 'Myanmar', 'NPL': 'Nepal', 'PRK': 'North Korea', 'OMN': 'Oman',
    'PAK': 'Pakistan', 'PHL': 'Philippines', 'QAT': 'Qatar', 'SGP': 'Singapore',
    'LKA': 'Sri Lanka', 'SYR': 'Syria', 'TJK': 'Tajikistan', 'THA': 'Thailand',
    'TKM': 'Turkmenistan', 'ARE': 'United Arab Emirates', 'UZB': 'Uzbekistan',
    'VNM': 'Vietnam', 'YEM': 'Yemen', 'KAZ': 'Kazakhstan', 'IDN': 'Indonesia',
    
    // Europe
    'ALB': 'Albania', 'AUT': 'Austria', 'BLR': 'Belarus', 'BEL': 'Belgium',
    'BIH': 'Bosnia', 'BGR': 'Bulgaria', 'HRV': 'Croatia', 'CZE': 'Czechia',
    'DNK': 'Denmark', 'EST': 'Estonia', 'FIN': 'Finland', 'GRC': 'Greece',
    'HUN': 'Hungary', 'ISL': 'Iceland', 'IRL': 'Ireland', 'XKX': 'Kosovo',
    'LVA': 'Latvia', 'LTU': 'Lithuania', 'LUX': 'Luxembourg', 'MDA': 'Moldova',
    'MNE': 'Montenegro', 'NLD': 'Netherlands', 'MKD': 'North Macedonia',
    'NOR': 'Norway', 'PRT': 'Portugal', 'ROU': 'Romania', 'SRB': 'Serbia',
    'SVK': 'Slovakia', 'SVN': 'Slovenia', 'SWE': 'Sweden', 'CHE': 'Switzerland',
    'UKR': 'Ukraine',
    
    // Oceania
    'FJI': 'Fiji', 'MHL': 'Marshall Islands', 'FSM': 'Micronesia', 'NZL': 'New Zealand',
    'PLW': 'Palau', 'PNG': 'Papua New Guinea', 'WSM': 'Samoa', 'SLB': 'Solomon Islands',
    'VUT': 'Vanuatu', 'NCL': 'New Caledonia',
    
    // Territories
    'HKG': 'Hong Kong', 'GUF': 'French Guiana', 'GLP': 'Guadeloupe', 'MTQ': 'Martinique',
    'REU': 'Réunion', 'FLK': 'Falkland Islands', 'ATA': 'Antarctica',
  }
  return NAMES[isoCode] || isoCode
}

export default { loadWorldGeo, getCountryName }
